import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Button,
  Text,
  VStack,
  Image,
  useBreakpointValue,
  useToast,
  useDisclosure,
  HStack,
  Divider,
  Icon,
  Badge,
  Heading,
  background,
} from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BoxesIcon,
  BoxIcon,
  CarFront,
  LogInIcon,
  LogOutIcon,
  Settings,
  ShoppingCart,
  User2,
  ChevronDown,
  Ticket,
  UsersRound,
  RefreshCw,
} from "lucide-react";
import Cart from "../Cart/Cart";
import { useAppSelector } from "../../store/hooks/hooks";
import { useAuth } from "../../auth/AuthContext";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../configs/firebase";
import { normalizeUserRole, USER_ROLES } from "../Profile/constants/userRoles";
import type { UserRole } from "../Profile/constants/userRoles";
import GlobalSearch from "../Search/GlobalSearch";
import { AddIcon } from "@chakra-ui/icons";
import DeliveryAddressButton from "../../deliveryModule/components/DeliveryAddressButton";
import DeliveryAddressModal from "../../deliveryModule/components/DeliveryAddressModal";
import { subscribeToCartOpen } from "../../utils/cartEvents";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import Logo from "../../assets/images/log.png";
import { getFooterColors } from "../Footer/PublicFooter";

type NavItem = {
  id: number;
  name: string;
  icon: React.ElementType;
  path: string;
  color: string;
  superAdminOnly?: boolean;
  adminOnly?: boolean;
};

const hexToRgba = (hex: string, alpha: number) => {
  const cleaned = hex.replace("#", "");

  if (cleaned.length !== 6) {
    return `rgba(100,116,139,${alpha})`;
  }

  const bigint = parseInt(cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const NAV_ACCENT = "#14b1a0";

export const navItems: NavItem[] = [
  {
    id: 1,
    name: "Hiking Manager",
    icon: CarFront,
    path: "/hike-manager",
    color: "#14B8A6",
  },
  {
    id: 2,
    name: "Orders",
    icon: BoxesIcon,
    path: "/admin/orders",
    color: "#2563EB",
  },
  {
    id: 3,
    name: "Deliveries",
    icon: CarFront,
    path: "/delivery/list",
    color: "#F97316",
  },
  {
    id: 4,
    name: "Products Manager",
    icon: ShoppingCart,
    path: "/manage/products",
    color: "#10B981",
  },
  {
    id: 5,
    name: "Car Hire Manager",
    icon: BoxesIcon,
    path: "/hire-manager",
    color: "#8B5CF6",
  },
  {
    id: 6,
    name: "Accommodations Manager",
    icon: BoxesIcon,
    path: "/travel/Accommodation-manager",
    color: "#EC4899",
  },
  {
    id: 7,
    name: "Finance",
    icon: BoxesIcon,
    path: "/finance/admin",
    color: "#0F766E",
  },
  {
    id: 8,
    name: "Driver Ops",
    icon: CarFront,
    path: "/drivers/admin",
    color: "#0891B2",
  },
  {
    id: 9,
    name: "Analytics",
    icon: BoxesIcon,
    path: "/analytics",
    color: "#6366F1",
  },
  { id: 10, name: "Profile", icon: User2, path: "/profile", color: "#0EA5E9" },
  {
    id: 11,
    name: "My Products",
    icon: BoxIcon,
    path: "/my-products",
    color: "#22C55E",
  },
  {
    id: 12,
    name: "Settings",
    icon: Settings,
    path: "/settings",
    color: "#F59E0B",
  },
  {
    id: 13,
    name: "Tickets",
    icon: Ticket,
    path: "/support/tickets",
    color: "#A855F7",
  },
  {
    id: 14,
    name: "Inventory & Stock",
    icon: Ticket,
    path: "/manage/inventory",
    color: "#A855F7",
  },
  {
    id: 15,
    name: "Marketplace Sync",
    icon: RefreshCw,
    path: "/admin/marketplace-sync",
    color: "#0EA5E9",
    adminOnly: true,
  },
  {
    id: 16,
    name: "Users Admin",
    icon: UsersRound,
    path: "/admin/users",
    color: "#DC2626",
    superAdminOnly: true,
  },
];

const NavBar = () => {
  const navigate = useNavigate();
  const cart = useAppSelector((state) => state.cart);
  const cartItemCount = cart.items.reduce(
    (sum, item) => sum + (item.quantity || 0),
    0,
  );

  const [cartOpen, setCartOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [profileImageError, setProfileImageError] = useState(false);
  const [openTicketCount, setOpenTicketCount] = useState(0);
  const [currentUserRole, setCurrentUserRole] = useState<UserRole>(
    USER_ROLES.GUEST,
  );
  const minWidth = useBreakpointValue({ base: "280px", md: "340px" });

  const { user, logout } = useAuth();
  const toast = useToast();

  const {
    isOpen: isAddressOpen,
    onOpen: openAddressModal,
    onClose: closeAddressModal,
  } = useDisclosure();

  const { theme } = useCustomTheme();
  const isDark = theme.name === "dark";

  const pageText = theme.text;
  const mutedText = theme.textMuted;
  const subtleText = theme.textSubtle;

  const cardBg = theme.surface;
  const softBg = theme.surfaceAlt;

  const borderColor = theme.border;
  const hoverBg = theme.hover;
  const panelShadow = theme.shadowMd;
  const location = useLocation();
  const isSuperAdmin = currentUserRole === USER_ROLES.SUPER_ADMIN;
  const hasAdminAccess =
    currentUserRole === USER_ROLES.ADMIN ||
    currentUserRole === USER_ROLES.SUPER_ADMIN;

  const defaultIconColor = isDark
    ? theme.text
    : theme.brand?.deepSlate || theme.text;
  const navBg = theme.surface;
  const cardsBorderRadii ="8px";

  const handleLogout = async () => {
    await logout();
    toast({
      title: "Sign Out",
      description: "You are now logged out!",
      status: "success",
      duration: 8000,
      isClosable: true,
    });
    navigate("/dashboard");
  };

  const getUserInitials = (name: string) => {
    if (!name) return "U";
    const names = name.trim().split(" ").filter(Boolean);
    return names
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  };

  const renderContainedIcon = (
    IconComponent: React.ElementType,
    color: string,
    active = false,
    size: string = "34px",
  ) => (
    <Flex
      w={size}
      h={size}
      borderRadius={cardsBorderRadii}
      align="center"
      justify="center"
      bg={hexToRgba(color, active ? 0.22 : 0.14)}
      border={`1px solid ${hexToRgba(color, active ? 0.34 : 0.22)}`}
      boxShadow={active ? theme.shadowSm : "none"}
      transition="all 0.2s ease"
      flexShrink={0}
    >
      <Icon
        as={IconComponent}
        boxSize={"14px"}
        color={color}
        fill={hexToRgba(color, active ? 0.16 : 0.1)}
        strokeWidth={2.2}
      />
    </Flex>
  );

  useEffect(() => {
    let isMounted = true;

    const fetchCurrentUserRole = async () => {
      if (!user?.uid) {
        if (isMounted) {
          setCurrentUserRole(USER_ROLES.GUEST);
        }
        return;
      }

      try {
        const userSnapshot = await getDoc(doc(db, "users", user.uid));

        if (!isMounted) return;

        if (!userSnapshot.exists()) {
          setCurrentUserRole(USER_ROLES.GUEST);
          return;
        }

        setCurrentUserRole(normalizeUserRole(userSnapshot.data()?.role));
      } catch (error) {
        console.error("Failed to load navigation user role:", error);

        if (isMounted) {
          setCurrentUserRole(USER_ROLES.GUEST);
        }
      }
    };

    fetchCurrentUserRole();

    return () => {
      isMounted = false;
    };
  }, [user?.uid]);

  useEffect(() => {
    const unsubscribe = subscribeToCartOpen(() => {
      setCartOpen(true);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    setProfileImageError(false);
  }, [user?.photoURL, user?.uid]);

  useEffect(() => {
    let isMounted = true;

    const fetchOpenTickets = async () => {
      if (!user?.uid) {
        setOpenTicketCount(0);
        return;
      }

      try {
        const response = await fetch("/api/support/tickets", {
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch tickets: ${response.status}`);
        }

        const data = await response.json();
        const tickets = Array.isArray(data)
          ? data
          : Array.isArray(data?.tickets)
            ? data.tickets
            : Array.isArray(data?.data)
              ? data.data
              : [];

        const openStatuses = new Set([
          "open",
          "pending",
          "in_progress",
          "in-progress",
          "new",
          "assigned",
        ]);

        const count = tickets.filter((ticket: any) => {
          const ticketUserId =
            ticket?.userId ||
            ticket?.customerId ||
            ticket?.createdBy ||
            ticket?.ownerId;

          const belongsToUser =
            !ticketUserId || String(ticketUserId) === String(user.uid);
          const status = String(ticket?.status || "")
            .toLowerCase()
            .trim();

          return belongsToUser && openStatuses.has(status);
        }).length;

        if (isMounted) {
          setOpenTicketCount(count);
        }
      } catch (error) {
        console.error("Failed to fetch open tickets:", error);
        if (isMounted) {
          setOpenTicketCount(0);
        }
      }
    };

    fetchOpenTickets();

    return () => {
      isMounted = false;
    };
  }, [user?.uid]);

  return (
    <>
      <Box
        position="sticky"
        top={0}
        zIndex={20}
        pt={{ base: 1, md: 2 }}
        bg="transparent"
      >
        <Flex
          px={{ base: 3, md: 4, lg: 5 }}
          h={{ base: "66px", md: "72px" }}
          align="center"
          justify="space-between"
          gap={{ base: 2, md: 3 }}
          bg={navBg}
          color={pageText}
          boxShadow={
            isDark
              ? "0 1px 2px rgba(0, 0, 0, 0.18)"
              : "0 1px 2px rgba(15, 23, 42, 0.07)"
          }
          backdropFilter="blur(12px)"
          position="relative"
          overflow="visible"
          _after={{
            content: '""',
            position: "absolute",
            left: 3,
            right: 3,
            bottom: 0,
            height: "1px",
            bg: borderColor,
            opacity: 0.9,
          }}
        >
          <Box
            position="absolute"
            inset={0}
            pointerEvents="none"
            borderRadius={{ base: cardsBorderRadii, md: cardsBorderRadii }}
            bgGradient={
              isDark
                ? "linear(to-b, rgba(255,255,255,0.045), transparent 30%)"
                : "linear(to-b, rgba(255,255,255,0.42), transparent 30%)"
            }
          />

          <Flex
            ml="0px"
            flexWrap="nowrap"
            alignItems="center"
            cursor="pointer"
            onClick={() => navigate("/dashboard")}
            userSelect="none"
            role="button"
            tabIndex={0}
            aria-label="Go to dashboard"
            sx={{
              WebkitUserSelect: "none",
              WebkitTapHighlightColor: "transparent",
              caretColor: "transparent",
            }}
          >
            <Image
              h="26px"
              src={Logo}
              draggable={false}
              pointerEvents="none"
            />

            <Heading
                as="h2"
                ml="8px"
                fontSize="2xl"
                fontWeight="800"
                color={"#30333a"}
                letterSpacing="-0.6px"
                lineHeight="1"
                whiteSpace="nowrap"
              >
                Pick
                <Text as="span" color={NAV_ACCENT}>
                  Dengu
                </Text>
              </Heading>
          </Flex>

          <Flex
            flex="1"
            justify="center"
            minW={0}
            zIndex={1}
            px={{ base: 0, md: 2 }}
          >
            <Box
              w="100%"
              maxW={{ base: "100%", md: "640px", xl: "720px" }}
              sx={
                isDark
                  ? undefined
                  : {
                      "& .chakra-input__group": {
                        boxShadow: "none !important",
                      },
                      "& .chakra-input": {
                        boxShadow: "none !important",
                      },
                      "& [role='combobox']": {
                        boxShadow: "none !important",
                      },
                    }
              }
            >
              <GlobalSearch />
            </Box>
          </Flex>

          <HStack
            spacing={{ base: 1, md: 2 }}
            align="center"
            minW="fit-content"
            zIndex={1}
          >
            {!user && (
              <Button
                onClick={() => navigate("/login")}
                leftIcon={<LogInIcon size={16} />}
                fontSize="sm"
                fontWeight="700"
                color={theme.textOnPrimary}
                bgImage={theme.gradients.primaryButton}
                _hover={{
                  bgImage: theme.gradients.primaryButtonHover,
                  transform: "translateY(-1px)",
                }}
                _active={{ transform: "translateY(0)" }}
                borderRadius={theme.radii.sm}
                boxShadow={theme.shadowSm}
                transition="all 0.2s ease"
                px={{ base: 3, md: 4 }}
              >
                Sign In
              </Button>
            )}

            <HStack
              spacing={2}
              display={{ base: "none", xl: "flex" }}
              align="center"
              pl={1}
              pr={1}
            >
              {user && (
                <Box
                  p="2px"
                  borderRadius={cardsBorderRadii}
                  bg={
                    isDark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.7)"
                  }
                >
                  <DeliveryAddressButton />
                </Box>
              )}

              <Button
                onClick={() => navigate("/hike-manager")}
                leftIcon={<AddIcon boxSize={4} color={theme.textOnPrimary} />}
                color={theme.textOnPrimary}
                bgImage={
                  theme.gradients.secondaryButton ||
                  theme.gradients.primaryButton
                }
                _hover={{
                  bgImage:
                    theme.gradients.secondaryButtonHover ||
                    theme.gradients.primaryButtonHover,
                  transform: "translateY(-1px)",
                }}
                _active={{ transform: "translateY(0)" }}
                borderRadius="5px"
                boxShadow={theme.shadowSm}
                fontWeight="700"
                transition="all 0.2s ease"
              >
                Add Trip
              </Button>
            </HStack>

            <Box
              position="relative"
              onMouseEnter={() => setCartOpen(true)}
              onMouseLeave={() => setCartOpen(false)}
            >
              <Button
                leftIcon={<ShoppingCart size={18} />}
                variant="ghost"
                onClick={() => setCartOpen((prev) => !prev)}
                borderRadius={cardsBorderRadii}
                color={defaultIconColor}
                _hover={{ bg: hoverBg }}
                px={{ base: 2, md: 3 }}
                minW={{ base: "40px", md: "44px" }}
              ></Button>

              <Flex
                position="absolute"
                onClick={() => setCartOpen((prev) => !prev)}
                cursor="pointer"
                top="-2px"
                right={{ base: "2px", md: "4px" }}
                bgImage={theme.gradients.primaryButton}
                color={theme.textOnPrimary}
                borderRadius={cardsBorderRadii}
                alignItems="center"
                justifyContent="center"
                h="20px"
                minW="20px"
                px={1}
                fontSize="10px"
                fontWeight="800"
                boxShadow={theme.shadowSm}
              >
                {cartItemCount}
              </Flex>

              {cartOpen && (
                <Box
                  position="absolute"
                  top="100%"
                  right="0"
                  pt="10px"
                  borderRadius={cardsBorderRadii}
                  minW={minWidth}
                  zIndex={30}
                  onMouseEnter={() => setCartOpen(true)}
                  onMouseLeave={() => setCartOpen(false)}
                >
                  <Box
                    bg={cardBg}
                    border={`1px solid ${borderColor}`}
                    borderRadius={cardsBorderRadii}
                    boxShadow={panelShadow}
                    overflow="hidden"
                    backdropFilter={isDark ? "blur(8px)" : undefined}
                    p={6}
                  >
                    <Cart onCheckout={() => setCartOpen(false)} />
                  </Box>
                </Box>
              )}
            </Box>

            {user && (
              <Box
                position="relative"
                onMouseEnter={() => setAccountOpen(true)}
                onMouseLeave={() => setAccountOpen(false)}
              >
                <Button
                  variant="ghost"
                  borderRadius={cardsBorderRadii}
                  pl="8px"
                  pr={{ base: "8px", md: "10px" }}
                  onClick={() => setAccountOpen((prev) => !prev)}
                  gap={2}
                  color={pageText}
                  _hover={{ bg: hoverBg }}
                  h={{ base: "40px", md: "42px" }}
                >
                  {user && user.photoURL && !profileImageError ? (
                    <Image
                      borderRadius={cardsBorderRadii}
                      boxSize="28px"
                      src={user.photoURL}
                      alt="Profile"
                      onError={() => setProfileImageError(true)}
                    />
                  ) : (
                    <Flex
                      align="center"
                      justify="center"
                      bg={softBg}
                      color={pageText}
                      borderRadius={cardsBorderRadii}
                      boxSize="28px"
                      fontSize="xs"
                      fontWeight="800"
                    >
                      {getUserInitials(user?.displayName || "")}
                    </Flex>
                  )}

                  <Box
                    display={{ base: "none", lg: "block" }}
                    textAlign="left"
                    lineHeight="1.1"
                  >
                    <Text fontSize="sm" fontWeight="700" color={pageText}>
                      My Account
                    </Text>
                    <Text fontSize="10px" color={subtleText}>
                      Menu & tools
                    </Text>
                  </Box>

                  <ChevronDown
                    size={16}
                    color={isDark ? "#cbd5e1" : "#64748b"}
                  />
                </Button>

                {accountOpen && (
                  <Box
                    position="absolute"
                    top="100%"
                    right="0"
                    pt="10px"
                    borderRadius={cardsBorderRadii}
                    minW={{ base: "280px", md: minWidth }}
                    zIndex={30}
                    onMouseEnter={() => setAccountOpen(true)}
                    onMouseLeave={() => setAccountOpen(false)}
                  >
                    <VStack
                      boxShadow={panelShadow}
                      borderRadius={cardsBorderRadii}
                      bg={cardBg}
                      border={`1px solid ${borderColor}`}
                      spacing={1}
                      align="stretch"
                      p={2}
                      overflow="hidden"
                      backdropFilter={isDark ? "blur(8px)" : undefined}
                      maxH={{
                        base: "calc(100vh - 84px)",
                        md: "calc(100vh - 96px)",
                      }}
                      display="flex"
                      flexDirection="column"
                    >
                      <Box
                        px={3}
                        py={2.5}
                        borderRadius={cardsBorderRadii}
                        bg={softBg}
                        border={`1px solid ${borderColor}`}
                        flexShrink={0}
                      >
                        <Text
                          fontSize="sm"
                          fontWeight="700"
                          color={pageText}
                          noOfLines={1}
                        >
                          {user.displayName || "User"}
                        </Text>
                        <Text fontSize="xs" color={subtleText} noOfLines={1}>
                          {user.email || "Signed in"}
                        </Text>
                      </Box>

                      <Divider
                        borderColor={borderColor}
                        my={1}
                        flexShrink={0}
                      />

                      <Box
                        flex="1"
                        minH={0}
                        overflowY="auto"
                        pr={1}
                        sx={{
                          scrollbarWidth: "thin",
                          scrollbarColor: isDark
                            ? "rgba(255,255,255,0.18) transparent"
                            : "rgba(15,23,42,0.18) transparent",
                          "&::-webkit-scrollbar": {
                            width: "6px",
                          },
                          "&::-webkit-scrollbar-track": {
                            background: "transparent",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            background: isDark
                              ? "rgba(255,255,255,0.12)"
                              : "rgba(15,23,42,0.14)",
                            borderRadius: cardsBorderRadii,
                          },
                        }}
                      >
                        <VStack spacing={1} align="stretch">
                          {navItems
                            .filter(
                              (item) =>
                                (!item.superAdminOnly || isSuperAdmin) &&
                                (!item.adminOnly || hasAdminAccess),
                            )
                            .map((item) => {
                              const isActive = location.pathname.startsWith(
                                item.path,
                              );
                              const isTickets =
                                item.path === "/support/tickets";

                              return (
                                <Button
                                  key={item.id}
                                  onClick={() => {
                                    setAccountOpen(false);
                                    navigate(item.path);
                                  }}
                                  variant="ghost"
                                  justifyContent="flex-start"
                                  fontWeight="600"
                                  fontSize="sm"
                                  borderRadius={cardsBorderRadii}
                                  color={isActive ? pageText : mutedText}
                                  bg={
                                    isActive
                                      ? theme.gradients?.tabHikesSelected ||
                                        theme.selected
                                      : "transparent"
                                  }
                                  border={`1px solid ${
                                    isActive
                                      ? `${theme.brand?.electricBlue || theme.primary}33`
                                      : "transparent"
                                  }`}
                                  boxShadow={isActive ? theme.shadowSm : "none"}
                                  _hover={{
                                    bg: isActive
                                      ? theme.gradients?.tabHikesSelected ||
                                        theme.selected
                                      : hoverBg,
                                    color: pageText,
                                    transform: "translateY(-1px)",
                                  }}
                                  _active={{ transform: "translateY(0)" }}
                                  transition="all 0.2s ease"
                                  h="48px"
                                  px={3}
                                  flexShrink={0}
                                >
                                  <Flex
                                    align="center"
                                    justify="space-between"
                                    w="100%"
                                  >
                                    <HStack spacing={3} minW={0}>
                                      {renderContainedIcon(
                                        item.icon,
                                        item.color,
                                        isActive,
                                        "32px",
                                      )}
                                      <Text noOfLines={1}>{item.name}</Text>
                                    </HStack>

                                    <HStack spacing={2} flexShrink={0}>
                                      {isTickets && openTicketCount > 0 && (
                                        <Badge
                                          borderRadius={cardsBorderRadii}
                                          px={2}
                                          py={0.5}
                                          bg={
                                            isDark
                                              ? "rgba(168,85,247,0.18)"
                                              : "rgba(168,85,247,0.12)"
                                          }
                                          color={
                                            isDark ? "purple.200" : "purple.700"
                                          }
                                          border={`1px solid ${
                                            isDark
                                              ? "rgba(168,85,247,0.28)"
                                              : "rgba(168,85,247,0.18)"
                                          }`}
                                          fontSize="10px"
                                          fontWeight="800"
                                        >
                                          {openTicketCount}
                                        </Badge>
                                      )}

                                      {isActive && (
                                        <Box
                                          w="8px"
                                          h="8px"
                                          borderRadius={cardsBorderRadii}
                                          bg={item.color}
                                          boxShadow={`0 0 0 3px ${hexToRgba(item.color, 0.14)}`}
                                        />
                                      )}
                                    </HStack>
                                  </Flex>
                                </Button>
                              );
                            })}

                          <Divider borderColor={borderColor} my={1} />

                          <Button
                            bg="transparent"
                            justifyContent="flex-start"
                            fontWeight="700"
                            fontSize="sm"
                            color={mutedText}
                            _hover={{
                              bg: hoverBg,
                              color: pageText,
                              transform: "translateY(-1px)",
                            }}
                            _active={{ transform: "translateY(0)" }}
                            onClick={handleLogout}
                            borderRadius={cardsBorderRadii}
                            transition="all 0.2s ease"
                            h="48px"
                            px={3}
                            flexShrink={0}
                          >
                            <Flex
                              align="center"
                              justify="space-between"
                              w="100%"
                            >
                              <HStack spacing={3}>
                                {renderContainedIcon(
                                  LogOutIcon,
                                  "#EF4444",
                                  false,
                                  "32px",
                                )}
                                <Text>Logout</Text>
                              </HStack>
                            </Flex>
                          </Button>
                        </VStack>
                      </Box>
                    </VStack>
                  </Box>
                )}
              </Box>
            )}
          </HStack>
        </Flex>
      </Box>

      <DeliveryAddressModal
        isOpen={isAddressOpen}
        onClose={closeAddressModal}
      />
    </>
  );
};

export default NavBar;
